import { Alert, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';
import { useCreateBrand } from 'src/graphql/entities/brand';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { ClientSegmentFormFields } from './client-segment-form-fields';
import {
  ClientSegmentFormField,
  type ClientSegmentFormValues,
  DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
} from './client-segment-form.types';

export const ClientSegmentAddModal = () => {
  const { isOpened, modalProps } = useModalState(Modals.ClientSegmentAddModal);
  const { closeModal } = useModalMethods(Modals.ClientSegmentAddModal);
  const { showSuccessAlert } = useNotifications();
  const { createBrand } = useCreateBrand();

  const partnerId = modalProps?.partnerId ?? null;

  const formMethods = useForm<ClientSegmentFormValues>({
    defaultValues: DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
  } = formMethods;

  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const close = useCallback(() => {
    reset(DEFAULT_CLIENT_SEGMENT_FORM_VALUES);
    setSubmissionError(null);
    closeModal();
  }, [closeModal, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (partnerId === null) {
      setSubmissionError('No partner selected.');
      return;
    }
    setSubmissionError(null);

    const created = await createBrand({
      partnerId,
      name: values[ClientSegmentFormField.Name].trim(),
      displayName: values[ClientSegmentFormField.DisplayName].trim(),
    });

    if (!created) {
      setSubmissionError('Could not create client segment.');
      return;
    }

    showSuccessAlert('Client segment created.');
    close();
  });

  return (
    <ModalContainer
      open={isOpened}
      size="large"
      title="Add client segment"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'client-segment-add-cancel' },
        {
          label: 'Create segment',
          kind: 'primary',
          onClick: onSubmit,
          disabled: !isValid,
          loading: isSubmitting,
          testId: 'client-segment-add-submit',
        },
      ]}
      onClose={close}
      data-testid="client-segment-add-modal"
    >
      <GenericForm<ClientSegmentFormValues> formMethods={formMethods}>
        <Typography variant="body2" color="text.secondary">
          Create a brand-facing identity for one of your audiences.
        </Typography>
        {submissionError ? (
          <Alert severity="error" data-testid="client-segment-modal-error">
            {submissionError}
          </Alert>
        ) : null}
        <ClientSegmentFormFields disabled={isSubmitting} />
      </GenericForm>
    </ModalContainer>
  );
};
